/**
 * "Accept and commit" confirmation for step 3 (f10.6, design D10): lines new /
 * refreshed / absent, mappings and aliases to be created, unmapped values left
 * open and the step's progress after commit. Every number is the server's.
 */

import { Check, Info } from 'lucide-react';

import type { KimbleReview } from '@/api/client';
import { Modal } from '@/routes/reference-data/Modal';

import { refusalMessage } from './kimbleDumpView';

export function KimbleDumpCommitDialog({
  review,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  review: KimbleReview;
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const plan = review.commit_plan;
  const stale = plan.later_steps_becoming_stale;
  const completes = plan.committed_scopes_after_commit === plan.expected_scopes;
  return (
    <Modal
      title="Accept and commit?"
      subtitle={`${review.batch.scope?.name ?? 'Kimble dump'} · ${
        review.batch.original_filename ?? `batch #${review.batch.batch_number}`
      }`}
      onClose={onClose}
      onSubmit={onConfirm}
      busy={busy}
      submitLabel="Accept and commit"
      submitIcon={<Check aria-hidden="true" />}
      layout="plain"
      message={refusalMessage(error)}
    >
      <dl className="dl cols-2 mb-4">
        <div>
          <dt>Forecast lines</dt>
          <dd>
            {plan.lines_new} new &middot; {plan.lines_refreshed} refreshed &middot; {plan.lines_absent} absent
          </dd>
        </div>
        <div>
          <dt>Step after commit</dt>
          <dd>
            {plan.committed_scopes_after_commit} of {plan.expected_scopes}
            {completes ? ' — complete' : ''}
          </dd>
        </div>
        <div>
          <dt>Resource BU mappings created</dt>
          <dd>
            {plan.mappings_to_add.length
              ? plan.mappings_to_add
                  .map((m) => `${m.raw_value} → ${m.canonical_psu}${m.country_code ? ` / ${m.country_code}` : ''}`)
                  .join(', ')
              : 'None'}
          </dd>
        </div>
        <div>
          <dt>Unit aliases recorded</dt>
          <dd>
            {plan.aliases_to_add.length
              ? plan.aliases_to_add.map((a) => `${a.alias} → ${a.business_unit}`).join(', ')
              : 'None'}
          </dd>
        </div>
        <div>
          <dt>Left unmapped</dt>
          <dd>
            {plan.unmapped_bu_values
              ? `${plan.unmapped_bu_values} Resource BU value(s) left unmapped (${plan.unmapped_bu_rows} rows)`
              : 'None'}
          </dd>
        </div>
        <div>
          <dt>Later completed steps become stale</dt>
          <dd>{completes && stale.length ? stale.join(', ') : 'None'}</dd>
        </div>
      </dl>
      <div className="alert alert-info">
        <Info aria-hidden="true" />
        <div className="a-body">
          The commit runs as a background job and writes everything in one transaction &mdash; all of it, or
          nothing. Lines are never deleted; a key missing from this dump is flagged absent.
        </div>
      </div>
    </Modal>
  );
}
